import { useState } from 'react';
import type { ReactNode } from 'react';
import { fetchAnalytics } from '@/api/dashboard';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { DonutChart, TrendChart } from '@/components/charts/Charts';
import type { DonutSegment } from '@/components/charts/Charts';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { refundModeLabel } from '@/constants/defaults';
import { downloadCsv } from '@/lib/csv';
import { lastNDaysRange, rangeDayCount } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { formatCompactCurrency, formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { toast } from '@/store/useToastStore';
import type {
  AnalyticsResponse,
  AnalyticsFunnel,
  AnalyticsCustomerBehavior,
  AnalyticsRoi,
} from '@/types';
import { RoiWalletIcon, RoiExchangeIcon, RoiBankIcon, RoiClockIcon, RoiBarsIcon } from '@/components/icons';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'reason', label: 'By reason' },
  { id: 'product', label: 'By product' },
  { id: 'region', label: 'By region' },
  { id: 'courier', label: 'Courier performance' },
  { id: 'customer', label: 'Customer behavior' },
  { id: 'roi', label: 'ROI calculator' },
];

const REFUND_MODE_COLORS: Record<string, string> = {
  STORE_CREDIT: '#7c3aed',
  ORIGINAL_PAYMENT: '#2563eb',
  GIFT_CARD: '#4b5563',
  DISCOUNT_CODE: '#d97706',
  UPI_TRANSFER: '#059669',
};

/** Show sub-day averages in hours (e.g. `18.00 h`) and longer ones in days. */
function formatResolutionTime(avgDays: number): string {
  if (avgDays < 1) {
    return `${(avgDays * 24).toFixed(2)} h`;
  }
  return `${avgDays.toFixed(2)} days`;
}

const PRODUCT_GRADIENTS = [
  'from-blue-400 to-blue-600',
  'from-amber-500 to-amber-700',
  'from-emerald-400 to-emerald-600',
  'from-rose-400 to-rose-600',
  'from-violet-400 to-violet-600',
];

const FUNNEL_STEPS: { key: keyof AnalyticsFunnel; label: string; color: string }[] = [
  { key: 'submitted', label: 'Submitted', color: 'bg-brand' },
  { key: 'approved', label: 'Approved', color: 'bg-brand' },
  { key: 'pickedUp', label: 'Picked up', color: 'bg-blue-500' },
  { key: 'receivedAtWarehouse', label: 'Received at WH', color: 'bg-blue-500' },
  { key: 'qcPassed', label: 'QC passed', color: 'bg-emerald-500' },
  { key: 'resolved', label: 'Refunded/Exchanged', color: 'bg-emerald-500' },
];

type TileTone = 'brand' | 'success' | 'info' | 'warning' | 'danger';

/** Soft circular icon-chip classes for stat / ROI tiles. */
const TILE_TONES: Record<TileTone, string> = {
  brand: 'bg-brand-soft text-brand',
  success: 'bg-success-soft text-success',
  info: 'bg-info-soft text-info',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
};

export function AnalyticsScreen() {
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));
  const days = rangeDayCount(range);
  const [tab, setTab] = useState('overview');
  const { data, errorMessage, isLoading, reload } = useAsyncData<AnalyticsResponse | null>(
    (signal) => fetchAnalytics(range, signal),
    [range.from, range.to],
  );

  function handleExport() {
    if (!data) {
      return;
    }
    const rows: (string | number)[][] = [['Analytics', `Last ${days} days`], []];
    rows.push(['Reason', 'Count', 'Percent', 'Avg refund']);
    (data.byReason ?? []).forEach((row) =>
      rows.push([row.label || row.slug, row.count, `${row.pct}%`, row.avgRefundAmount ?? '']),
    );
    rows.push([], ['Refund mode', 'Count', 'Percent', 'Amount']);
    (data.byRefundMode ?? []).forEach((row) =>
      rows.push([refundModeLabel(row.mode), row.count, `${row.pct}%`, row.totalAmount]),
    );
    rows.push([], ['Product', 'Returns', 'Top reason']);
    (data.topProducts ?? []).forEach((row) => rows.push([row.productTitle, row.returns, row.topReason ?? '']));

    if (data.byRegion?.length) {
      rows.push([], ['Region', 'Count', 'Percent', 'Refund amount', 'NDR', 'Avg resolution (d)']);
      data.byRegion.forEach((row) =>
        rows.push([
          row.region,
          row.count,
          `${row.pct}%`,
          row.totalRefundAmount,
          row.ndrCount ?? '',
          row.avgResolutionDays ?? '',
        ]),
      );
    }

    const behavior = data.customerBehavior;
    if (behavior) {
      rows.push([], ['Top returners', 'Returns', 'Total refund']);
      (behavior.topReturners ?? []).forEach((row) =>
        rows.push([row.customerName || row.customerEmail || '—', row.returns, row.totalRefundAmount]),
      );
    }
    if (data.roi) {
      rows.push([], ['ROI', 'Amount']);
      rows.push(['Gross refund value', data.roi.grossRefundValue]);
      rows.push(['Cash refunded', data.roi.cashRefunded]);
      rows.push(['Saved via credit', data.roi.savedViaCredit]);
      rows.push(['Saved via exchange', data.roi.savedViaExchange]);
      rows.push(['Total revenue saved', data.roi.totalRevenueSaved]);
      rows.push(['Processing cost', data.roi.estimatedProcessingCost]);
      rows.push(['Net savings', data.roi.netSavings]);
    }
    downloadCsv(`analytics-${days}d.csv`, rows);
    toast.success('Analytics exported');
  }

  const totalRefunded = (data?.byRefundMode ?? []).reduce((sum, row) => sum + (row.totalAmount ?? 0), 0);
  const changePositive = (data?.changePct ?? 0) >= 0;

  return (
    <div className="animate-fade-in pb-10">
      <PageHeader
        title="Analytics"
        description={`Last ${days} days · compared to previous ${days} days`}
        actions={
          <>
            <DateRangePicker value={range} onChange={setRange} />
            <Button variant="secondary" size="sm" onClick={handleExport} disabled={!data}>
              Export CSV
            </Button>
          </>
        }
      />

      <div className="mb-5 overflow-x-auto border-b border-line">
        <div className="flex min-w-max gap-1">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              className={cn(
                'whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors',
                tab === entry.id ? 'border-ink text-ink' : 'border-transparent text-ink-muted hover:text-ink',
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <LoadingState message="Crunching the numbers…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : !data ? null : tab === 'overview' ? (
        <div className="flex flex-col gap-4">
          {/* Summary tiles */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label="Total returns"
              value={formatNumber(data.totalReturns)}
              tone="brand"
              icon={<RoiBarsIcon />}
            />
            <StatTile
              label="Avg resolution"
              value={formatResolutionTime(data.resolutionTime?.avgDays ?? 0)}
              tone="info"
              icon={<RoiClockIcon />}
            />
            <StatTile
              label="Net savings"
              value={data.roi ? formatCompactCurrency(data.roi.netSavings) : '—'}
              tone="success"
              icon={<RoiWalletIcon />}
            />
            <StatTile
              label="Repeat rate"
              value={data.customerBehavior ? `${data.customerBehavior.repeatRate}%` : '—'}
              tone="warning"
              icon={<RoiExchangeIcon />}
            />
          </div>

          {/* Returns over time */}
          <Card>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-ink">Returns over time</h2>
                <div className="mt-2 flex items-center gap-3">
                  <span className="text-4xl font-bold text-ink">{formatNumber(data.totalReturns)}</span>
                  <span
                    className={cn(
                      'inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-sm font-semibold',
                      changePositive ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger',
                    )}
                  >
                    {changePositive ? '↑' : '↓'} {Math.abs(data.changePct)}% vs prev {days}d
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-ink-muted">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-brand" /> This period
                </span>
              </div>
            </div>
            <div className="mt-4">
              <TrendChart points={data.overTime ?? []} />
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Resolution funnel */}
            <Card>
              <h2 className="text-sm font-semibold text-ink">Resolution funnel</h2>
              <p className="mt-0.5 text-sm text-ink-muted">
                From submission to refund · {formatNumber(data.funnel.submitted)} requests
              </p>
              <div className="mt-4 flex flex-col gap-3.5">
                {FUNNEL_STEPS.map((step) => {
                  const value = data.funnel[step.key] ?? 0;
                  const pct = data.funnel.submitted ? Math.round((value / data.funnel.submitted) * 100) : 0;
                  return (
                    <div key={step.key}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="text-ink">{step.label}</span>
                        <span className="font-medium text-ink-muted">
                          {formatNumber(value)} · {pct}%
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
                        <div className={cn('h-full rounded-full', step.color)} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Refund mode mix */}
            <Card>
              <h2 className="text-sm font-semibold text-ink">Refund modes</h2>
              <p className="mt-0.5 text-sm text-ink-muted">Where the money went</p>
              {data.byRefundMode.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-muted">No refunds in this period.</p>
              ) : (
                <div className="mt-4 flex flex-wrap items-center justify-center gap-6 sm:justify-between">
                  <DonutChart
                    segments={data.byRefundMode.map<DonutSegment>((row) => ({
                      label: refundModeLabel(row.mode),
                      value: row.count,
                      color: REFUND_MODE_COLORS[row.mode] ?? '#9ca3af',
                      pct: row.pct,
                      amount: row.totalAmount,
                    }))}
                    centerTop={formatCompactCurrency(totalRefunded)}
                    centerBottom="Total refunded"
                  />
                  <ul className="flex flex-col gap-2.5">
                    {data.byRefundMode.map((row) => (
                      <li key={row.mode} className="flex items-center gap-3 text-sm">
                        <span
                          className="h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                          style={{ backgroundColor: REFUND_MODE_COLORS[row.mode] ?? '#9ca3af' }}
                        />
                        <span className="flex-1 text-ink">{refundModeLabel(row.mode)}</span>
                        <span className="font-semibold text-ink">{row.pct}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

          {/* Most-returned products */}
          <ProductList products={data.topProducts} limit={6} title="Most-returned products" />
        </div>
      ) : tab === 'reason' ? (
        <Card>
          <h2 className="text-sm font-semibold text-ink">Returns by reason</h2>
          <div className="mt-4 flex flex-col gap-3.5">
            {data.byReason.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-muted">No returns in this period.</p>
            ) : (
              data.byReason.map((row) => (
                <div key={row.slug}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-ink">{row.label || row.slug}</span>
                    <span className="font-medium text-ink-muted">
                      {formatNumber(row.count)} · {row.pct}%
                      {row.avgRefundAmount != null && (
                        <span className="text-ink-subtle"> · avg {formatCurrency(row.avgRefundAmount)}</span>
                      )}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
                    <div className="h-full rounded-full bg-brand" style={{ width: `${row.pct}%` }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      ) : tab === 'product' ? (
        <ProductList products={data.topProducts} title="Returns by product" />
      ) : tab === 'region' ? (
        <RegionPanel rows={data.byRegion ?? []} />
      ) : tab === 'courier' ? (
        <CourierPanel rows={data.courierPerformance ?? []} />
      ) : tab === 'customer' ? (
        <CustomerPanel behavior={data.customerBehavior} />
      ) : (
        <RoiPanel roi={data.roi} days={days} resolutionDays={data.resolutionTime?.avgDays} />
      )}
    </div>
  );
}

function RegionPanel({ rows }: { rows: AnalyticsResponse['byRegion'] }) {
  const list = rows ?? [];
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">Returns by region</h2>
      </div>
      {list.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-ink-muted">No region data for this period.</p>
      ) : (
        <div className="flex flex-col gap-4 px-5 py-4">
          {list.map((row) => (
            <div key={row.region}>
              <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-ink">{row.region}</span>
                <span className="flex-shrink-0 font-medium text-ink-muted">
                  {formatNumber(row.count)} · {row.pct}%
                  <span className="text-ink-subtle"> · {formatCurrency(row.totalRefundAmount)}</span>
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
                <div className="h-full rounded-full bg-brand" style={{ width: `${row.pct}%` }} />
              </div>
              <div className="mt-1 flex gap-4 text-xs text-ink-subtle">
                {row.avgResolutionDays != null && <span>{formatResolutionTime(row.avgResolutionDays)} avg resolution</span>}
                {row.ndrCount != null && <span>{formatNumber(row.ndrCount)} NDR</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function StatTile({
  label,
  value,
  icon,
  tone = 'brand',
}: {
  label: string;
  value: string;
  icon?: ReactNode;
  tone?: TileTone;
}) {
  return (
    <Card>
      {icon ? (
        <span
          className={cn(
            'mb-3 flex h-10 w-10 items-center justify-center rounded-full',
            TILE_TONES[tone],
          )}
        >
          {icon}
        </span>
      ) : null}
      <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</p>
      <p className="mt-1.5 text-2xl font-bold text-ink">{value}</p>
    </Card>
  );
}

function CourierPanel({ rows }: { rows: AnalyticsResponse['courierPerformance'] }) {
  const list = rows ?? [];
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">Courier performance</h2>
      </div>
      {list.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-ink-muted">No courier data for this period yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                <th className="px-5 py-3 font-semibold">Courier</th>
                <th className="px-3 py-3 text-right font-semibold">Shipments</th>
                <th className="px-3 py-3 text-right font-semibold">Success rate</th>
                <th className="px-3 py-3 text-right font-semibold">Successful</th>
                <th className="px-3 py-3 text-right font-semibold">Pending</th>
                <th className="px-3 py-3 text-right font-semibold">Failed</th>
                <th className="px-3 py-3 text-right font-semibold">NDRs</th>
                <th className="px-3 py-3 text-right font-semibold">Avg attempts</th>
                <th className="px-5 py-3 text-right font-semibold">Avg days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((row) => (
                <tr key={row.courierName}>
                  <td className="px-5 py-3 font-semibold text-ink">{row.courierName}</td>
                  <td className="px-3 py-3 text-right text-ink-muted">{formatNumber(row.totalShipments)}</td>
                  <td className="px-3 py-3 text-right">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-semibold',
                        row.successRate >= 80
                          ? 'bg-success-soft text-success'
                          : row.successRate >= 50
                            ? 'bg-warning-soft text-warning'
                            : 'bg-danger-soft text-danger',
                      )}
                    >
                      {row.successRate}%
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right text-ink-muted">{formatNumber(row.successfulPickups)}</td>
                  <td className="px-3 py-3 text-right text-ink-muted">{formatNumber(row.pendingPickups)}</td>
                  <td className="px-3 py-3 text-right text-ink-muted">{formatNumber(row.failedPickups)}</td>
                  <td className="px-3 py-3 text-right text-ink-muted">{formatNumber(row.totalNdrs)}</td>
                  <td className="px-3 py-3 text-right text-ink-muted">{row.avgAttemptsToPickup.toFixed(1)}</td>
                  <td className="px-5 py-3 text-right text-ink-muted">{row.avgDaysToPickup.toFixed(1)}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function CustomerPanel({ behavior }: { behavior: AnalyticsCustomerBehavior | undefined }) {
  if (!behavior) {
    return (
      <Card>
        <p className="py-8 text-center text-sm text-ink-muted">No customer data for this period.</p>
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Unique customers"
          value={formatNumber(behavior.totalUniqueCustomers)}
          tone="brand"
          icon={<RoiBarsIcon />}
        />
        <StatTile
          label="Repeat customers"
          value={formatNumber(behavior.repeatCustomers)}
          tone="info"
          icon={<RoiExchangeIcon />}
        />
        <StatTile label="Repeat rate" value={`${behavior.repeatRate}%`} tone="warning" icon={<RoiBarsIcon />} />
        <StatTile
          label="Exchange rate"
          value={`${behavior.exchangeRate}%`}
          tone="success"
          icon={<RoiExchangeIcon />}
        />
      </div>
      <Card padded={false} className="overflow-hidden">
        <div className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">Top returners</h2>
        </div>
        {behavior.topReturners.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-ink-muted">No returners in this period.</p>
        ) : (
          <ul className="divide-y divide-line">
            {behavior.topReturners.map((row, index) => (
              <li
                key={row.customerEmail || row.customerName || index}
                className="flex items-center gap-4 px-5 py-3.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{row.customerName || '—'}</p>
                  {row.customerEmail && (
                    <p className="truncate text-xs text-ink-muted">{row.customerEmail}</p>
                  )}
                </div>
                <span className="w-20 flex-shrink-0 text-right text-sm font-bold text-ink">
                  {formatNumber(row.returns)}
                </span>
                <span className="w-28 flex-shrink-0 text-right text-sm text-ink-muted">
                  {formatCurrency(row.totalRefundAmount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const PERIOD_LABELS: Record<number, string> = {
  7: 'this week',
  30: 'this month',
  90: 'this quarter',
  365: 'this year',
};

function RoiPanel({
  roi,
  days,
  resolutionDays,
}: {
  roi: AnalyticsRoi | undefined;
  days: number;
  resolutionDays?: number;
}) {
  if (!roi) {
    return (
      <Card>
        <p className="py-8 text-center text-sm text-ink-muted">No ROI data for this period.</p>
      </Card>
    );
  }

  const periodLabel = PERIOD_LABELS[days] ?? `in the last ${days} days`;
  const tiles: { label: string; value: string; hint: string; tone: TileTone; icon: ReactNode }[] = [
    {
      label: 'Revenue retained',
      value: formatCompactCurrency(roi.totalRevenueSaved),
      hint: 'Customers exchanged or took store credit instead of a cash refund',
      tone: 'brand',
      icon: <RoiBarsIcon />,
    },
    {
      label: 'Saved via store credit',
      value: formatCompactCurrency(roi.savedViaCredit),
      hint: 'Refunds kept in-store as store credit',
      tone: 'success',
      icon: <RoiWalletIcon />,
    },
    {
      label: 'Saved via exchange',
      value: formatCompactCurrency(roi.savedViaExchange),
      hint: 'Returns converted into exchanges instead of refunds',
      tone: 'info',
      icon: <RoiExchangeIcon />,
    },
    {
      label: 'Gross refund value',
      value: formatCompactCurrency(roi.grossRefundValue),
      hint: 'Total value of all returns processed this period',
      tone: 'warning',
      icon: <span className="text-base font-semibold leading-none">₹</span>,
    },
    {
      label: 'Cash refunded',
      value: formatCompactCurrency(roi.cashRefunded),
      hint: 'Paid back to the original payment method',
      tone: 'danger',
      icon: <RoiBankIcon />,
    },
    {
      label: 'Faster resolution',
      value: resolutionDays != null ? `${resolutionDays.toFixed(1)}d` : '—',
      hint: `Average time to resolve a return · processing cost ${formatCompactCurrency(
        roi.estimatedProcessingCost,
      )}`,
      tone: 'brand',
      icon: <RoiClockIcon />,
    },
  ];

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-brand text-2xl font-bold text-white">
            ₹
          </span>
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-ink">
              Fastrr Return saved{' '}
              <span className="text-brand">{formatCompactCurrency(roi.netSavings)}</span> {periodLabel}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              Estimated based on exchange conversion, retained credit and operations time saved.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-2xl border border-line bg-surface p-5 shadow-card">
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full',
                  TILE_TONES[tile.tone],
                )}
              >
                {tile.icon}
              </span>
              <p className="text-sm font-medium text-ink-muted">{tile.label}</p>
            </div>
            <p className="mt-3 text-3xl font-bold tracking-tight text-ink">{tile.value}</p>
            <p className="mt-2.5 text-sm leading-snug text-ink-muted">{tile.hint}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ProductList({
  products,
  title,
  limit,
}: {
  products: AnalyticsResponse['topProducts'];
  title: string;
  limit?: number;
}) {
  const rows = limit ? products.slice(0, limit) : products;
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-ink-muted">No product returns in this period.</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((product, index) => (
            <li key={product.shopifyProductId} className="flex items-center gap-4 px-5 py-3.5">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt=""
                  className="h-11 w-11 flex-shrink-0 rounded-lg object-cover"
                  loading="lazy"
                />
              ) : (
                <span
                  className={cn(
                    'h-11 w-11 flex-shrink-0 rounded-lg bg-gradient-to-br',
                    PRODUCT_GRADIENTS[index % PRODUCT_GRADIENTS.length],
                  )}
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">{product.productTitle}</p>
              </div>
              <span className="w-12 flex-shrink-0 text-right text-sm font-bold text-ink">
                {formatNumber(product.returns)}
              </span>
              <span className="w-32 flex-shrink-0 text-right text-sm text-ink-muted">
                {product.topReason ?? '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
